import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  PlusIcon,
  FunnelIcon,
  EllipsisVerticalIcon,
  PencilIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import {
  Card,
  Button,
  Table,
  SearchInput,
  Badge,
  Pagination,
  Dropdown,
  Select,
  ConfirmDialog,
  EmptyState,
} from '../components/ui';
import { useOrderStore } from '../stores';
import { usePermission } from '../hooks/usePermission';
import type { Order } from '../types';
import { formatDate, formatCurrency, getStatusDisplayName, getStatusBadgeVariant } from '../utils';

const statusOptions = [
  { value: '', label: 'All Statuses' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'REQUIRES_ACTION', label: 'Action Required' },
  { value: 'PROCESSING', label: 'Processing' },
  { value: 'SHIPPED', label: 'Shipped' },
  { value: 'DELIVERED', label: 'Delivered' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

export const Orders: React.FC = () => {
  const navigate = useNavigate();
  const canCreateOrder = usePermission('orders:create');
  const {
    orders,
    isLoading,
    totalCount,
    currentPage,
    pageSize,
    filters,
    fetchOrders,
    deleteOrder,
    setFilters,
    setPage,
  } = useOrderStore();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState<Order | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders, currentPage, filters]);

  const handleSearch = useCallback(
    (value: string) => {
      setFilters({ search: value });
    },
    [setFilters]
  );

  const handleStatusFilter = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFilters({ status: e.target.value, standardizedStatus: e.target.value });
  };

  const handlePageChange = (page: number) => {
    setPage(page);
  };

  const handleDeleteClick = (order: Order) => {
    setOrderToDelete(order);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!orderToDelete) return;
    setIsDeleting(true);
    try {
      await deleteOrder(orderToDelete.id);
      toast.success('Order deleted successfully');
      setDeleteDialogOpen(false);
      setOrderToDelete(null);
    } catch (error) {
      toast.error('Failed to delete order');
    } finally {
      setIsDeleting(false);
    }
  };

  const columns = [
    {
      key: 'id',
      header: 'Order ID',
      render: (order: Order) => (
        <span className="font-mono text-sm text-secondary-900 dark:text-secondary-100">
          #{order.id.slice(0, 8)}
        </span>
      ),
    },
    {
      key: 'patient',
      header: 'Patient',
      render: (order: Order) => (
        <div>
          <p className="font-medium text-secondary-900 dark:text-secondary-100">
            {order.patient?.firstName} {order.patient?.lastName}
          </p>
          <p className="text-sm text-secondary-500 dark:text-secondary-400">{order.patient?.email}</p>
        </div>
      ),
    },
    {
      key: 'medication',
      header: 'Medication',
      render: (order: Order) => {
        const itemCount = order.orderItems?.length || 0;
        if (itemCount > 1) {
          return (
            <div>
              <p className="font-medium text-secondary-900 dark:text-secondary-100">{itemCount} products</p>
            </div>
          );
        }
        const offeringName = order.orderItems?.[0]?.offering?.offeringName || order.offering?.offeringName;
        const qty = order.orderItems?.[0]?.offering?.quantity || order.offering?.quantity || order.quantity;
        return (
          <div>
            <p className="font-medium text-secondary-900 dark:text-secondary-100">{offeringName || '—'}</p>
            {qty && <p className="text-sm text-secondary-500 dark:text-secondary-400">Qty: {qty}</p>}
          </div>
        );
      },
    },
    {
      key: 'status',
      header: 'Status',
      render: (order: Order) => {
        // Use standardizedStatus from API if available, fall back to order.status
        const statusToDisplay = order.standardizedStatus || order.status;
        return (
          <Badge variant={getStatusBadgeVariant(statusToDisplay)} dot>
            {getStatusDisplayName(statusToDisplay)}
          </Badge>
        );
      },
    },
    {
      key: 'totalAmount',
      header: 'Amount',
      render: (order: Order) => (
        <span className="font-medium text-secondary-900 dark:text-secondary-100">{formatCurrency(order.totalAmount)}</span>
      ),
    },
    {
      key: 'lastStatusUpdateAt',
      header: 'Updated',
      sortable: true,
      render: (order: Order) => (
        <span className="text-secondary-600 dark:text-secondary-400">{formatDate(order.lastStatusUpdateAt || order.updatedAt)}</span>
      ),
    },
    {
      key: 'actions',
      header: '',
      className: 'w-12',
      render: (order: Order) => (
        <Dropdown
          trigger={
            <button
              className="p-1 rounded-lg hover:bg-secondary-100 dark:hover:bg-secondary-700 transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              <EllipsisVerticalIcon className="h-5 w-5 text-secondary-500 dark:text-secondary-400" />
            </button>
          }
          items={[
            {
              label: 'Edit',
              icon: <PencilIcon className="h-4 w-4" />,
              onClick: () => navigate(`/orders/${order.id}/edit`),
            },
            {
              label: 'Delete',
              icon: <TrashIcon className="h-4 w-4" />,
              onClick: () => handleDeleteClick(order),
              danger: true,
            },
          ]}
        />
      ),
    },
  ];

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900 dark:text-secondary-50">Orders</h1>
          <p className="text-secondary-500 dark:text-secondary-400">Manage and track your patient orders</p>
        </div>
        {canCreateOrder && (
          <Button leftIcon={<PlusIcon className="h-5 w-5" />} onClick={() => navigate('/orders/new')}>
            New Order
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card padding="sm">
        <div className="flex flex-col sm:flex-row gap-4">
          <SearchInput
            value={filters.search}
            onChange={handleSearch}
            placeholder="Search orders..."
            className="flex-1"
          />
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <FunnelIcon className="h-5 w-5 text-secondary-400 dark:text-secondary-500" />
              <Select
                options={statusOptions}
                value={filters.status || ''}
                onChange={handleStatusFilter}
                className="w-40"
              />
            </div>
          </div>
        </div>
      </Card>

      {/* Table */}
      <Card padding="none">
        {orders.length === 0 && !isLoading ? (
          <EmptyState
            title="No orders found"
            description={canCreateOrder ? "Get started by creating your first order" : "No orders have been created yet"}
            action={
              canCreateOrder ? (
                <Button
                  leftIcon={<PlusIcon className="h-5 w-5" />}
                  onClick={() => navigate('/orders/new')}
                >
                  Create Order
                </Button>
              ) : undefined
            }
          />
        ) : (
          <>
            <Table
              columns={columns}
              data={orders}
              keyExtractor={(order) => order.id}
              isLoading={isLoading}
              onRowClick={(order) => navigate(`/orders/${order.id}`)}
            />
            {totalPages > 1 && (
              <div className="p-4 border-t border-secondary-100 dark:border-secondary-700">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </>
        )}
      </Card>

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Order"
        message={`Are you sure you want to delete order #${orderToDelete?.id.slice(0, 8)}? This action cannot be undone.`}
        confirmLabel="Delete"
        isLoading={isDeleting}
      />
    </div>
  );
};

export default Orders;



